import React, { useState, useEffect, useContext } from "react";
import { useParams } from "react-router-dom";
import { Container, Row, Col, Input, Button, Form, FormGroup, Label } from "reactstrap";
import { useToast } from "../context/ToastContext";
import useAluno from "../context/hooks/useAluno";
import InputMask from "react-input-mask";
import { CursoContext } from '../context/CursoContext';
import { CursoType } from '../context/hooks/useCurso';
import { decryptId } from "../utils/cryptoUtils";

const CadastroAluno = () => {
    const { encryptedContratoId } = useParams();
    const [contratoId, setContratoId] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);
    const showToast = useToast();
    const { createAluno } = useAluno();
    const { handleGetCursoByEmpresaId, listCurso, handleGetCursoByContratoId, listCursoByContrato } = useContext(CursoContext);

    const [alunoData, setAlunoData] = useState({
        referenceCode: 0,
        name: "",
        email: "",
        cpf: "",
        address: "",
        addressNumber: "",
        neighborhood: "",
        city: "",
        state: "",
        zipCode: "",
        landline: "",
        telephone_01: "",
        telephone_02: "",
        telephone_03: "",
        telephone_04: "",
        telephone_05: "",
        telephone_06: "",
        facebook: "",
        instagram: "",
        keepThePhotos: false,
        hasAlbum: false,
        numberOfPhotos: 0,
        password: "",
        contratoId: 0,
        cursoId: 0,
        base64Image: "",
    });

    useEffect(() => {
        const decodeContratoId = async () => {
            if (encryptedContratoId) {
                const id = await decryptId(decodeURIComponent(encryptedContratoId));
                if (id) {
                    setAlunoData(prevState => ({
                        ...prevState,
                        contratoId: id
                    }));
                    setContratoId(id);
                } else {
                    showToast("error", "Erro: Contrato inválido.");
                }
            }
        };
        decodeContratoId();
    }, [encryptedContratoId]);

    useEffect(() => {
        if (contratoId !== null) {
            setLoading(true);
            handleGetCursoByContratoId(contratoId)
                .then(() => setLoading(false))
                .catch(error => {
                    console.log(error);
                    setLoading(false);
                });
        }
    }, [contratoId]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setAlunoData((prevState) => ({
            ...prevState,
            [name]: name === "cursoId" ? Number(value) : value,
        }));
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!contratoId) {
            showToast("error", "Erro: Identificador do contrato não encontrado.");
            return;
        }

        if (!alunoData.cursoId || alunoData.cursoId === 0) {
            showToast("error", "Erro: Selecione um curso.");
            return;
        }

        const hasPhone = [
            alunoData.landline,
            alunoData.telephone_01,
            alunoData.telephone_02,
            alunoData.telephone_03,
            alunoData.telephone_04,
            alunoData.telephone_05,
            alunoData.telephone_06
        ].some(phone => phone && phone.trim().length > 0);

        if (!hasPhone) {
            showToast("error", "Erro: Preencha pelo menos um telefone.");
            return;
        }

        const response = await createAluno(alunoData);

        if (response) {
            showToast("success", "Aluno cadastrado com sucesso!");
            setAlunoData({
                referenceCode: 0,
                name: "",
                email: "",
                cpf: "",
                address: "",
                addressNumber: "",
                neighborhood: "",
                city: "",
                state: "",
                zipCode: "",
                landline: "",
                telephone_01: "",
                telephone_02: "",
                telephone_03: "",
                telephone_04: "",
                telephone_05: "",
                telephone_06: "",
                facebook: "",
                instagram: "",
                keepThePhotos: false,
                hasAlbum: false,
                numberOfPhotos: 0,
                password: "",
                // contratoCursoGuid: contratoCursoGuid
                contratoId: Number(contratoId),
                cursoId: 0,
                base64Image: "",
            });
        } else {
            showToast("error", "Erro ao cadastrar aluno. Tente novamente.");
        }
    };

    const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onloadend = () => {
            setAlunoData(prev => ({
                ...prev,
                base64Image: reader.result?.toString() || ""
            }));
        };
        reader.readAsDataURL(file);
    };

    return (
        <Container className="mt-5">
            <Row className="justify-content-center">
                <Col md="12">
                    <h2 className="text-center mb-4">Cadastro de Aluno</h2>
                    <Form onSubmit={handleSubmit}>
                        <Row>
                            <Col md="6">
                                <FormGroup>
                                    <Label className="form-control-label" for="profileImage">Foto de Perfil</Label>
                                    <Input
                                        className="form-control-alternative"
                                        type="file"
                                        accept="image/*"
                                        id="profileImage"
                                        onChange={handleImageUpload}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="6">
                                <FormGroup>
                                    <Label className="form-control-label" for="referenceCode">Código de Referência</Label>
                                    <Input
                                        className="form-control-alternative"
                                        type="number"
                                        name="referenceCode"
                                        id="referenceCode"
                                        value={alunoData.referenceCode}
                                        placeholder="Código de referência"
                                        onChange={handleInputChange}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col md="6">
                                <FormGroup>
                                    <Label className="form-control-label" for="name">Nome</Label>
                                    <Input
                                        className="form-control-alternative"
                                        type="text"
                                        name="name"
                                        id="name"
                                        value={alunoData.name}
                                        placeholder="Digite seu nome"
                                        onChange={handleInputChange}
                                        required
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="6">
                                <FormGroup>
                                    <Label className="form-control-label" for="email">Email</Label>
                                    <Input
                                        className="form-control-alternative"
                                        type="email"
                                        name="email"
                                        id="email"
                                        value={alunoData.email}
                                        placeholder="Digite seu email"
                                        onChange={handleInputChange}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col md="6">
                                <FormGroup>
                                    <Label className="form-control-label" for="CPF">CPF</Label>
                                    <InputMask
                                        className="form-control form-control-alternative"
                                        mask="999.999.999-99"
                                        type="text"
                                        name="cpf"
                                        id="cpf"
                                        value={alunoData.cpf}
                                        placeholder="000.000.000-00"
                                        onChange={handleInputChange}
                                        required
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="6">
                                <FormGroup>
                                    <Label className="form-control-label" for="cursoId">Curso</Label>
                                    <Input
                                        type="select"
                                        name="cursoId"
                                        className="form-control-alternative"
                                        id="cursoId"
                                        value={alunoData.cursoId}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        <option value="0" disabled>Selecione um curso</option>
                                        {listCursoByContrato.length > 0 ? (
                                            listCursoByContrato.map((curso: CursoType) => (
                                                <option key={curso.id} value={curso.id}>
                                                    {curso.name}
                                                </option>
                                            ))
                                        ) : (
                                            <option value="0" disabled>Nenhum curso disponível</option>
                                        )}
                                    </Input>
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col md="4">
                                <FormGroup>
                                    <Label className="form-control-label" for="Address">Endereço</Label>
                                    <Input
                                        className="form-control-alternative"
                                        type="text"
                                        name="address"
                                        id="address"
                                        value={alunoData.address}
                                        placeholder="Endereço"
                                        onChange={handleInputChange}
                                        required
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="4">
                                <FormGroup>
                                    <Label className="form-control-label" for="addressNumber">Número</Label>
                                    <Input
                                        className="form-control-alternative"
                                        type="text"
                                        name="addressNumber"
                                        id="addressNumber"
                                        value={alunoData.addressNumber}
                                        placeholder="Número"
                                        onChange={handleInputChange}
                                        required
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="4">
                                <FormGroup>
                                    <Label className="form-control-label" for="neighborhood">Bairro</Label>
                                    <Input
                                        className="form-control-alternative"
                                        type="text"
                                        name="neighborhood"
                                        id="neighborhood"
                                        value={alunoData.neighborhood}
                                        placeholder="Bairro"
                                        onChange={handleInputChange}
                                        required
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col md="4">
                                <FormGroup>
                                    <Label className="form-control-label" for="zipCode">CEP</Label>
                                    <InputMask
                                        className="form-control form-control-alternative"
                                        mask="99999-999"
                                        type="text"
                                        name="zipCode"
                                        id="zipCode"
                                        value={alunoData.zipCode}
                                        placeholder="00000-000"
                                        onChange={handleInputChange}
                                        required
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="4">
                                <FormGroup>
                                    <Label className="form-control-label" for="state">Estado</Label>
                                    <Input
                                        className="form-control-alternative"
                                        type="text"
                                        name="state"
                                        id="state"
                                        value={alunoData.state}
                                        placeholder="Estado"
                                        onChange={handleInputChange}
                                        required
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="4">
                                <FormGroup>
                                    <Label className="form-control-label" for="city">Cidade</Label>
                                    <Input
                                        className="form-control-alternative"
                                        type="text"
                                        name="city"
                                        id="city"
                                        value={alunoData.city}
                                        placeholder="Cidade"
                                        onChange={handleInputChange}
                                        required
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col md="4">
                                <FormGroup>
                                    <Label className="form-control-label" for="landline">Telefone Fixo</Label>
                                    <InputMask
                                        className="form-control form-control-alternative"
                                        mask="(99) 9999-9999"
                                        type="text"
                                        name="landline"
                                        id="landline"
                                        value={alunoData.landline}
                                        placeholder="(00) 0000-0000"
                                        onChange={handleInputChange}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="4">
                                <FormGroup>
                                    <Label className="form-control-label" for="telephone_01">Telefone 01</Label>
                                    <InputMask
                                        className="form-control form-control-alternative"
                                        mask="(99) 99999-9999"
                                        type="text"
                                        name="telephone_01"
                                        id="telephone_01"
                                        value={alunoData.telephone_01}
                                        placeholder="(00) 00000-0000"
                                        onChange={handleInputChange}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="4">
                                <FormGroup>
                                    <Label className="form-control-label" for="telephone_02">Telefone 02</Label>
                                    <InputMask
                                        className="form-control form-control-alternative"
                                        mask="(99) 99999-9999"
                                        type="text"
                                        name="telephone_02"
                                        id="telephone_02"
                                        value={alunoData.telephone_02}
                                        placeholder="(00) 00000-0000"
                                        onChange={handleInputChange}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col md="6">
                                <FormGroup>
                                    <Label className="form-control-label" for="telephone_03">Telefone 03</Label>
                                    <InputMask
                                        className="form-control form-control-alternative"
                                        mask="(99) 99999-9999"
                                        type="text"
                                        name="telephone_03"
                                        id="telephone_03"
                                        value={alunoData.telephone_03}
                                        placeholder="(00) 00000-0000"
                                        onChange={handleInputChange}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="6">
                                <FormGroup>
                                    <Label className="form-control-label" for="telephone_04">Telefone 04</Label>
                                    <InputMask
                                        className="form-control form-control-alternative"
                                        mask="(99) 99999-9999"
                                        type="text"
                                        name="telephone_04"
                                        id="telephone_04"
                                        value={alunoData.telephone_04}
                                        placeholder="(00) 00000-0000"
                                        onChange={handleInputChange}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col md="6">
                                <FormGroup>
                                    <Label className="form-control-label" for="telephone_05">Telefone 05</Label>
                                    <InputMask
                                        className="form-control form-control-alternative"
                                        mask="(99) 99999-9999"
                                        type="text"
                                        name="telephone_05"
                                        id="telephone_05"
                                        value={alunoData.telephone_05}
                                        placeholder="(00) 00000-0000"
                                        onChange={handleInputChange}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="6">
                                <FormGroup>
                                    <Label className="form-control-label" for="telephone_06">Telefone 06</Label>
                                    <InputMask
                                        className="form-control form-control-alternative"
                                        mask="(99) 99999-9999"
                                        type="text"
                                        name="telephone_06"
                                        id="telephone_06"
                                        value={alunoData.telephone_06}
                                        placeholder="(00) 00000-0000"
                                        onChange={handleInputChange}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col md="6">
                                <FormGroup>
                                    <Label className="form-control-label" for="facebook">Facebook</Label>
                                    <Input
                                        className="form-control-alternative"
                                        type="text"
                                        name="facebook"
                                        id="facebook"
                                        value={alunoData.facebook}
                                        placeholder="Facebook"
                                        onChange={handleInputChange}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="6">
                                <FormGroup>
                                    <Label className="form-control-label" for="instagram">Instagram</Label>
                                    <Input
                                        className="form-control-alternative"
                                        type="text"
                                        name="instagram"
                                        id="instagram"
                                        value={alunoData.instagram}
                                        placeholder="Instagram"
                                        onChange={handleInputChange}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                        {/* <FormGroup>
                            <Label for="password">Senha</Label>
                            <Input
                                type="password"
                                name="password"
                                id="password"
                                value={alunoData.password}
                                placeholder="Crie uma senha"
                                onChange={handleInputChange}
                                required
                            />
                        </FormGroup> */}
                        <Button style={{ color: "#FFF", backgroundColor: "#585858", marginBottom: "60px" }} type="submit" block>
                            Cadastrar
                        </Button>
                    </Form>
                </Col>
            </Row>
        </Container>
    );
};

export default CadastroAluno;